"use client";
import { useState, type FormEvent } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";

interface SearchBarProps {
  defaultValue?: string;
  className?: string;
  placeholder?: string;
}

export function SearchBar({ defaultValue = "", className, placeholder }: SearchBarProps) {
  const [value, setValue] = useState(defaultValue);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    pushEvent(analyticsEvents.search_submit, { search_term: trimmed });
    // Full-page navigation (not router.push) so the search results page is a
    // fresh document — keeps ad behaviour consistent with every other link.
    window.location.assign(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <form
      role="search"
      onSubmit={onSubmit}
      className={cn(
        "border-border focus-within:ring-brand-200 flex w-full items-center rounded-full border bg-white shadow-sm focus-within:ring-2",
        className,
      )}
    >
      <Search aria-hidden className="text-muted-foreground ml-3 h-4 w-4" />
      <input
        type="search"
        name="q"
        autoComplete="off"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => pushEvent(analyticsEvents.search_focus, { source: "search-bar" })}
        placeholder={placeholder ?? "Search stickers, hashtags, packs..."}
        className="placeholder:text-muted-foreground w-full rounded-full bg-transparent px-3 py-2 text-sm outline-none"
        aria-label="Search stickers"
      />
      <button
        type="submit"
        className="bg-brand-500 hover:bg-brand-600 mr-1 rounded-full px-4 py-1.5 text-xs font-medium text-white transition"
      >
        Search
      </button>
    </form>
  );
}
